import { Signal, computed, effect, signal } from '@angular/core';
import { formatElapsedClock } from './duration.util';

/** Taktung der mitlaufenden Laufzeit. */
const CLOCK_TICK_MS = 1_000;

/**
 * Die aktuelle Zeit als Signal, das im Takt weiterspringt, solange {@code active} gilt.
 * Muss aus einem Injection-Kontext heraus gerufen werden; der Takt endet mit der Komponente.
 */
export function tickingNow(active: () => boolean, tickMs = CLOCK_TICK_MS): Signal<number> {
  const now = signal(Date.now());

  effect((onCleanup) => {
    if (!active()) {
      return;
    }
    now.set(Date.now());
    const handle = setInterval(() => now.set(Date.now()), tickMs);
    onCleanup(() => clearInterval(handle));
  });

  return now.asReadonly();
}

/**
 * Die mitlaufende Laufzeit eines Durchlaufs als {@code M:SS}, oder {@code null},
 * solange nichts laeuft (NEX-1232, NEX-1233).
 *
 * <p>Muss aus einem Injection-Kontext heraus gerufen werden — der Takt haengt an
 * einem {@link effect} und endet damit mit der Komponente, die ihn anlegt. Er laeuft
 * nur, solange ein Start vorliegt: ein beendeter Durchlauf braucht keinen
 * Sekundentakt, und ein weiterlaufender Timer hielte die Ansicht ohne Anlass wach.
 *
 * @param startedAt Zeitstempel des Starts, {@code null} wenn nichts laeuft.
 */
export function elapsedClock(startedAt: () => string | null): Signal<string | null> {
  const now = tickingNow(() => startedAt() !== null);

  return computed(() => {
    const started = startedAt();
    if (started === null) {
      return null;
    }
    const parsed = Date.parse(started);
    return Number.isNaN(parsed) ? null : formatElapsedClock(now() - parsed);
  });
}
